import React from 'react';
import '../../assets/styles/Workflow.css';

const STAGES = ['pending_build', 'in_progress', 'qa_review', 'ready_to_deliver', 'delivered'];
const STAGE_LABEL: Record<string, string> = {
  pending_cx_review: 'Pending CxEx Review',
  cx_correction_needed: 'Needs Correction (CxEx)',
  pending_build: 'Pending Build',
  in_progress: 'In Progress',
  qa_review: 'QA Review',
  ready_to_partially_deliver: 'Ready to Partially Deliver',
  partially_delivered: 'Partially Delivered',
  ready_to_deliver: 'Ready to Deliver',
  delivered: 'Delivered',
  rejected: 'Rejected',
};

interface StageStepperProps {
  stage: string;
}

const StageStepper: React.FC<StageStepperProps> = ({ stage }) => {
  // Rejected is a terminal exit, not a step in the normal 1-5 progression -
  // shown as its own banner instead of forcing it into the linear stepper.
  if (stage === 'rejected') {
    return <div className="wf-stepper-rejected">❌ This project has been rejected</div>;
  }

  // The CxEx review gate is a pre-pipeline phase for 'fulfillment' projects
  // only - it doesn't fit the 1-5 Pending Build...Delivered progression
  // (which starts only once CxEx has cleared it), so like Rejected above it
  // gets its own banner instead of a step position.
  if (stage === 'pending_cx_review') {
    return <div className="wf-stepper-cx-pending">🔍 Awaiting Customer Experience review</div>;
  }
  if (stage === 'cx_correction_needed') {
    return <div className="wf-stepper-cx-flagged">✏️ Sent back by Customer Experience - needs correction</div>;
  }

  // A project can cycle through ready_to_partially_deliver/partially_delivered
  // many times (once per batch) before ever reaching the real final stages -
  // that doesn't fit a straight 1-5 line, so both map onto the same visual
  // position as "Ready to Deliver" here. The delivery-history list (shown
  // alongside this stepper) is what actually distinguishes each round.
  const effectiveStage =
    stage === 'ready_to_partially_deliver' || stage === 'partially_delivered'
      ? 'ready_to_deliver'
      : stage;
  const currentIndex = STAGES.indexOf(effectiveStage);
  // The final stage (Delivered) has no "next" step to be merely in-progress
  // toward - reaching it is full completion, so it renders as done/checked
  // like every earlier passed step, not as a current/in-progress number.
  const lastIndex = STAGES.length - 1;
  return (
    <div className="wf-stepper">
      {STAGES.map((s, i) => {
        const isDone = i < currentIndex || (i === currentIndex && i === lastIndex);
        const isCurrent = i === currentIndex && i !== lastIndex;
        return (
          <div key={s} className={`wf-step ${isDone ? 'done' : isCurrent ? 'current' : ''}`}>
            <div className="wf-step-line" />
            <div className="wf-step-node">{isDone ? '✓' : i + 1}</div>
            <div className="wf-step-label">{STAGE_LABEL[s]}</div>
          </div>
        );
      })}
    </div>
  );
};

export const stageChipClass = (stage: string) => `wf-stage-chip wf-stage-${stage}`;
export const stageLabel = (stage: string) => STAGE_LABEL[stage] || stage;

export default StageStepper;
